import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Pulse, Warning } from "@phosphor-icons/react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const COLOR = {
  real: "#40B6A2",
  sombra: "#E8A33D",
  muted: "#6b89a8",
  danger: "#FF3B30",
  grid: "#123a5e",
};

const brl = (n) =>
  n != null
    ? n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 })
    : "—";

const usd = (n) =>
  n != null ? n.toLocaleString("pt-BR", { maximumFractionDigits: 0 }) : "—";

const spread = (n) => (n != null ? n.toFixed(4) : "—");

const pct = (n) => (n != null ? `${n.toFixed(1)}%` : "—");

// sigma vem por minuto em fração; anualizar ajuda a julgar se o número é plausível
// (252 pregões x 8h x 60 = 120.960 minutos de mercado por ano).
const sigmaAnual = (s) => (s != null ? `${(s * Math.sqrt(120960) * 100).toFixed(1)}%` : "—");

const fmtDia = (iso) => {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
};

function Card({ label, value, sub, accent }) {
  return (
    <div className="border border-[#123a5e] rounded-sm bg-[#0B2D4D] p-4 flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-[#6b89a8]">{label}</span>
      <span className="text-xl font-mono-data" style={{ color: accent || "#fff" }}>{value}</span>
      {sub && <span className="text-[11px] text-[#6b89a8]">{sub}</span>}
    </div>
  );
}

function Aviso({ children }) {
  return (
    <div className="border border-[#123a5e] rounded-sm bg-[#0B2D4D] p-4 flex gap-3 items-start">
      <Warning weight="bold" className="w-4 h-4 mt-0.5 shrink-0" style={{ color: COLOR.sombra }} />
      <p className="text-xs text-[#b4c9dd] leading-relaxed">{children}</p>
    </div>
  );
}

function SegmentoVazio({ titulo, nota }) {
  return (
    <div className="border border-[#123a5e] rounded-sm bg-[#0B2D4D] p-6 flex flex-col gap-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-white">{titulo}</h2>
      <p className="text-xs text-[#6b89a8]">{nota}</p>
    </div>
  );
}

export default function SpreadSombra() {
  const hoje = new Date().toISOString().slice(0, 10);
  const trintaDias = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);

  const [de, setDe] = useState(trintaDias);
  const [ate, setAte] = useState(hoje);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/spread-sombra/resumo", { params: { from: de, to: ate } });
      setDados(data);
    } catch {
      toast.error("Erro ao carregar o comparativo da fase sombra");
    } finally {
      setLoading(false);
    }
  }, [de, ate]);

  useEffect(() => { carregar(); }, [carregar]);

  const fixo = dados?.spread_fixo;
  const juros = dados?.juros_compostos;
  const cfg = dados?.config;

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Pulse weight="bold" className="w-5 h-5" style={{ color: COLOR.sombra }} />
            <div>
              <h1 className="text-lg font-semibold tracking-wide uppercase text-white">Spread Sombra</h1>
              <p className="text-xs text-[#6b89a8] mt-0.5">
                Simulação paralela por volatilidade — nunca aplicada a nenhuma trava
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={de} onChange={(e) => setDe(e.target.value)}
              className="bg-[#0B2D4D] border border-[#123a5e] rounded-sm px-2 py-1 text-xs text-white" />
            <span className="text-[#6b89a8] text-xs">até</span>
            <input type="date" value={ate} onChange={(e) => setAte(e.target.value)}
              className="bg-[#0B2D4D] border border-[#123a5e] rounded-sm px-2 py-1 text-xs text-white" />
          </div>
        </div>

        {/* Config vigente */}
        {cfg && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-[#6b89a8] border border-[#123a5e] rounded-sm bg-[#071b2e] px-4 py-3">
            <span>fórmula <b className="text-white font-mono-data">{cfg.formula_versao}</b></span>
            <span>k ≤5d <b className="text-white font-mono-data">{cfg.fator_k_ate_5}</b></span>
            <span>k ≤60d <b className="text-white font-mono-data">{cfg.fator_k_ate_60}</b></span>
            <span>k &gt;60d <b className="text-white font-mono-data">{cfg.fator_k_acima_60}</b></span>
            <span>teto <b className="text-white font-mono-data">R$ {spread(cfg.teto_brl)}</b></span>
            <span>teto relativo <b className="text-white font-mono-data">{cfg.teto_rel}×</b></span>
            <span>janela <b className="text-white font-mono-data">{cfg.janela_min} min</b></span>
            <span>horizonte <b className="text-white font-mono-data">{cfg.horizonte_min} min</b></span>
            <span style={{ color: cfg.ativo ? COLOR.real : COLOR.danger }}>
              {cfg.ativo ? "coleta ativa" : "COLETA DESLIGADA"}
            </span>
          </div>
        )}

        {loading && <p className="text-xs text-[#6b89a8]">Carregando…</p>}

        {!loading && fixo && fixo.n_travas === 0 && juros.n_travas === 0 && (
          <SegmentoVazio
            titulo="Nenhuma trava registrada no período"
            nota="A sombra grava uma linha por trava criada em /travas. Travas manuais ficam de fora por definição. Ajuste o período ou aguarde novas travas."
          />
        )}

        {!loading && fixo && fixo.n_travas > 0 && (
          <>
            {/* KPIs do segmento principal */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-white mb-1">
                Spread de risco <span className="text-[#6b89a8] font-normal normal-case">(regra atual + Spot + Spread)</span>
              </h2>
              <p className="text-[11px] text-[#6b89a8] mb-3">
                É este o segmento que a fase sombra quer avaliar: o spread de risco cobrado por faixa de prazo.
                Reúne a regra vigente e o Spot + Spread anterior — as eras se separam pela versão da fórmula.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card label="Travas" value={fixo.n_travas} sub={`${usd(fixo.volume_usd)} USD`} />
                <Card label="Receita real" value={brl(fixo.receita_real)} accent={COLOR.real} />
                <Card label="Receita simulada" value={brl(fixo.receita_simulada)} accent={COLOR.sombra} />
                <Card
                  label="Diferença"
                  value={brl(fixo.delta_brl)}
                  sub={fixo.delta_pct != null ? `+${fixo.delta_pct.toFixed(1)}% sobre a real` : null}
                  accent={COLOR.sombra}
                />
                <Card
                  label="Cobertura do dado"
                  value={pct(fixo.cobertura_pct)}
                  sub={`σ médio ${sigmaAnual(fixo.sigma_min_medio)} a.a.`}
                  accent={fixo.cobertura_pct != null && fixo.cobertura_pct < 90 ? COLOR.danger : "#fff"}
                />
              </div>
            </div>

            {fixo.cobertura_pct != null && fixo.cobertura_pct < 90 && (
              <Aviso>
                Só <b>{pct(fixo.cobertura_pct)}</b> das travas tinham volatilidade disponível no momento da
                criação. Abaixo de 90% o agregado não sustenta decisão — o problema está na cadência do feed
                (campo 100005), não na fórmula.
              </Aviso>
            )}

            {fixo.n_teto_atingido > 0 && (
              <Aviso>
                <b>{fixo.n_teto_atingido}</b> de {fixo.n_travas} travas bateram o teto. Onde o teto morde, o
                valor simulado é o teto, não o que a fórmula pediria — o delta destas linhas é um piso, não a
                estimativa real.
              </Aviso>
            )}

            {/* Série diária */}
            {fixo.serie.length > 0 && (
              <div className="border border-[#123a5e] rounded-sm bg-[#0B2D4D] p-4">
                <h3 className="text-xs uppercase tracking-wider text-[#6b89a8] mb-3">
                  Diferença por dia · volatilidade média
                </h3>
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={fixo.serie} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke={COLOR.grid} vertical={false} />
                    <XAxis dataKey="dia" tickFormatter={fmtDia} stroke={COLOR.muted} fontSize={11} />
                    <YAxis yAxisId="l" stroke={COLOR.sombra} fontSize={11} />
                    <YAxis yAxisId="r" orientation="right" stroke={COLOR.muted} fontSize={11}
                      tickFormatter={(v) => (v * Math.sqrt(120960) * 100).toFixed(0) + "%"} />
                    <Tooltip
                      contentStyle={{ background: "#061729", border: `1px solid ${COLOR.grid}`, fontSize: 12 }}
                      labelStyle={{ color: "#fff" }}
                      formatter={(v, nome) =>
                        nome === "delta_brl" ? [brl(v), "Diferença"] : [sigmaAnual(v) + " a.a.", "Volatilidade"]
                      }
                      labelFormatter={fmtDia}
                    />
                    <Bar yAxisId="l" dataKey="delta_brl" fill={COLOR.sombra} name="delta_brl" />
                    <Line yAxisId="r" type="monotone" dataKey="sigma_min_medio" stroke={COLOR.real}
                      dot={false} strokeWidth={2} name="sigma_min_medio" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Por faixa de prazo */}
            <div className="border border-[#123a5e] rounded-sm bg-[#0B2D4D] overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#6b89a8] border-b border-[#123a5e]">
                    <th className="p-3 text-left">Faixa de prazo</th>
                    <th className="p-3 text-right">Travas</th>
                    <th className="p-3 text-right">Volume USD</th>
                    <th className="p-3 text-right">Receita real</th>
                    <th className="p-3 text-right">Receita simulada</th>
                    <th className="p-3 text-right">Diferença</th>
                    <th className="p-3 text-right">Teto batido</th>
                  </tr>
                </thead>
                <tbody>
                  {fixo.por_faixa.map((f) => (
                    <tr key={f.faixa} className="border-b border-[#123a5e] last:border-0">
                      <td className="p-3 text-white">{f.rotulo}</td>
                      <td className="p-3 text-right font-mono-data text-[#b4c9dd]">{f.n_travas}</td>
                      <td className="p-3 text-right font-mono-data text-[#b4c9dd]">{usd(f.volume_usd)}</td>
                      <td className="p-3 text-right font-mono-data" style={{ color: COLOR.real }}>{brl(f.receita_real)}</td>
                      <td className="p-3 text-right font-mono-data" style={{ color: COLOR.sombra }}>{brl(f.receita_simulada)}</td>
                      <td className="p-3 text-right font-mono-data" style={{ color: COLOR.sombra }}>
                        {brl(f.delta_brl)}
                        {f.delta_pct != null && <span className="text-[#6b89a8]"> ({f.delta_pct.toFixed(0)}%)</span>}
                      </td>
                      <td className="p-3 text-right font-mono-data text-[#b4c9dd]">{f.n_teto_atingido}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Segmento de juros compostos — separado de propósito */}
        {!loading && juros && (
          juros.n_travas > 0 ? (
            <div className="border border-[#123a5e] rounded-sm bg-[#0B2D4D] p-4 space-y-3">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-white">
                  Juros compostos <span className="text-[#6b89a8] font-normal normal-case">(segmento à parte)</span>
                </h2>
                <p className="text-[11px] text-[#6b89a8] mt-1 leading-relaxed">
                  Aqui o valor cobrado é o carrego de juros (spot × ((1+i)^meses − 1)), não um spread de risco —
                  fica uma ordem de grandeza acima do spread fixo. Somar os dois no mesmo total tornaria a
                  diferença ilegível, por isso este bloco nunca entra no agregado acima.
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card label="Travas" value={juros.n_travas} sub={`${usd(juros.volume_usd)} USD`} />
                <Card label="Carrego real" value={brl(juros.receita_real)} accent={COLOR.real} />
                <Card label="Carrego simulado" value={brl(juros.receita_simulada)} accent={COLOR.sombra} />
                <Card label="Diferença" value={brl(juros.delta_brl)} accent={COLOR.sombra} />
              </div>
            </div>
          ) : (
            <SegmentoVazio
              titulo="Juros compostos"
              nota="Nenhuma trava de juros compostos no período. Quando houver, aparece aqui em separado — o valor cobrado ali é carrego, não spread de risco."
            />
          )
        )}

        {/* Últimas travas */}
        {!loading && dados?.ultimas?.length > 0 && (
          <div className="border border-[#123a5e] rounded-sm bg-[#0B2D4D] overflow-x-auto">
            <h3 className="text-xs uppercase tracking-wider text-[#6b89a8] p-3 border-b border-[#123a5e]">
              Últimas travas registradas
            </h3>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#6b89a8] border-b border-[#123a5e]">
                  <th className="p-3 text-left">Trava</th>
                  <th className="p-3 text-left">Tipo</th>
                  <th className="p-3 text-right">Prazo</th>
                  <th className="p-3 text-right">Volume USD</th>
                  <th className="p-3 text-right">Real</th>
                  <th className="p-3 text-right">Sugerido</th>
                  <th className="p-3 text-right">Impacto</th>
                  <th className="p-3 text-left">Volatilidade</th>
                </tr>
              </thead>
              <tbody>
                {dados.ultimas.map((l) => (
                  <tr key={l.trava_codigo} className="border-b border-[#123a5e] last:border-0">
                    <td className="p-3 font-mono-data text-white">{l.trava_codigo}</td>
                    {/* NULL = regra de set/2026 (fixo até o corte, % do spot
                        acima). Checagem explícita: `null` é falsy e apareceria
                        como "Juros compostos", que é justamente a era que a
                        regra nova encerrou. */}
                    <td className="p-3 text-[#b4c9dd]">
                      {l.com_juros == null ? "Regra atual" : l.com_juros ? "Spread fixo" : "Juros compostos"}
                    </td>
                    <td className="p-3 text-right font-mono-data text-[#b4c9dd]">{l.prazo_dias}d</td>
                    <td className="p-3 text-right font-mono-data text-[#b4c9dd]">{usd(l.volume_usd)}</td>
                    <td className="p-3 text-right font-mono-data" style={{ color: COLOR.real }}>{spread(l.spread_real)}</td>
                    <td className="p-3 text-right font-mono-data" style={{ color: COLOR.sombra }}>
                      {spread(l.spread_sugerido)}
                      {l.teto_atingido && <span className="text-[#6b89a8]" title="teto atingido"> ▲</span>}
                    </td>
                    <td className="p-3 text-right font-mono-data" style={{ color: COLOR.sombra }}>{brl(l.impacto_brl)}</td>
                    <td className="p-3 text-[#6b89a8]">
                      {l.vol_disponivel
                        ? `${sigmaAnual(l.sigma_min)} a.a. · ${l.n_ticks} ticks`
                        : (l.motivo_indisponivel || "indisponível")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </Layout>
  );
}
